import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { DaylightCommonContent } from "../../../../modules/content/components/DaylightCommonContent/DaylightCommonContent";
import { DaylightMedia } from "../../../../modules/content/components/DaylightMedia/DaylightMedia";
import { DaylightRichText } from "../../../../modules/content/components/DaylightRichText/DaylightRichText";
import { DaylightSection } from "../../../../modules/content/components/DaylightSection/DaylightSection";
import type { IFeaturedTestimonialBlockProps } from "./FeaturedTestimonialBlock.props";

export const FeaturedTestimonialBlock = ({
  alignment,
  anchorId,
  buttons,
  description,
  eyebrow,
  heading,
  headingEffect,
  parentClass,
  spacing,
  testimonial,
}: IFeaturedTestimonialBlockProps) => {
  if (!eyebrow && !heading && !description && !testimonial?.quote) return null;
  const Link = getComponent("Link");

  return (
    <DaylightSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-10 md:gap-14">
        <DaylightCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
          headingEffect={headingEffect}
          reveal
        />
        {testimonial ? (
          <figure
            className="grid gap-px overflow-hidden rounded-[14px] border border-border bg-border md:grid-cols-[minmax(14rem,0.75fr)_1.25fr]"
            data-reveal="stack"
            data-reveal-enter="focus"
          >
            {testimonial.portrait ? (
              <DaylightMedia
                className="min-w-0 rounded-none"
                framed={false}
                imageClassName="object-cover"
                media={testimonial.portrait.image}
                effect={testimonial.portrait.effect}
                sizes="(min-width: 768px) 32vw, calc(100vw - 48px)"
              />
            ) : null}
            <div
              className={cn(
                "relative flex min-w-0 flex-col justify-between gap-8 bg-surface-100 p-7 md:p-10 lg:p-12",
                !testimonial.portrait && "md:col-span-full",
              )}
            >
              <span
                aria-hidden="true"
                className="font-daylight-display text-5xl leading-[0.6] text-primary"
              >
                “
              </span>
              <blockquote className="flex-1">
                <DaylightRichText
                  className="font-daylight-display text-[clamp(1.5rem,2.6vw,2.25rem)] font-semibold leading-[1.15] tracking-[-0.025em] text-foreground"
                  content={testimonial.quote}
                />
              </blockquote>
              <figcaption className="flex flex-col gap-4 border-t border-border pt-5">
                <div>
                  <p className="font-daylight-body text-sm font-semibold text-foreground">
                    {testimonial.name}
                  </p>
                  {testimonial.attribution ? (
                    <p className="mt-1 font-daylight-mono text-xs text-ash">
                      {testimonial.attribution}
                    </p>
                  ) : null}
                </div>
                {testimonial.link ? (
                  <Link
                    aria-label={testimonial.link.accessibilityLabel || undefined}
                    className="signal-label inline-flex min-h-11 w-fit items-center text-primary underline decoration-primary/40 underline-offset-4 transition-colors hover:decoration-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    href={testimonial.link.href}
                    rel={testimonial.link.openInNewTab ? "noopener noreferrer" : undefined}
                    target={testimonial.link.openInNewTab ? "_blank" : undefined}
                  >
                    {testimonial.link.label}
                  </Link>
                ) : null}
              </figcaption>
            </div>
          </figure>
        ) : null}
      </div>
    </DaylightSection>
  );
};

FeaturedTestimonialBlock.displayName = "Block::FeaturedTestimonialBlock";
